import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FaFileAlt, FaCalendarDay, FaChartBar, FaCalendarAlt, FaPrint,
  FaMoneyBillWave, FaListAlt, FaWallet, FaArrowUp, FaArrowDown,
  FaTag, FaClipboardList
} from 'react-icons/fa';
import Header from '../others/Header';
import config from '../config/config';
import { useToast } from '../components/ToastContext';

const { API_BASE_URL } = config;

function numeroParaReais(valor) {
  if (isNaN(valor) || valor === null) return 'R$ 0,00';
  return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function dataHojeFormatada() {
  return new Date().toISOString().split('T')[0];
}

function mesAtualFormatado() {
  const hoje = new Date();
  const mes = String(hoje.getMonth() + 1).padStart(2, '0');
  return `${hoje.getFullYear()}-${mes}`;
}

function nomeMesAno(mesFiltro) {
  const [ano, mes] = mesFiltro.split('-').map(Number);
  const data = new Date(ano, mes - 1, 1);
  const nome = data.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  return nome.charAt(0).toUpperCase() + nome.slice(1);
}

const Relatorios = () => {
  const [tipoRelatorio, setTipoRelatorio] = useState('DIARIO'); // 'DIARIO' | 'MENSAL' | 'RESUMIDO'
  const { addToast } = useToast();

  const getAuthHeader = () => {
    const token = localStorage.getItem('jwtToken');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  return (
    <div style={styles.container}>
      <Header />
      <div style={styles.content}>
        <div style={styles.header}>
          <FaFileAlt style={styles.titleIcon} />
          <h1 style={styles.title}>Relatórios Financeiros</h1>
        </div>

        {/* ══════════ CARDS DE SELEÇÃO ══════════ */}
        <div style={styles.cardsSelecao}>
          <div
            onClick={() => setTipoRelatorio('DIARIO')}
            style={{ ...styles.cardTipo, ...(tipoRelatorio === 'DIARIO' && styles.cardTipoAtivo) }}
          >
            <FaCalendarDay style={styles.cardTipoIcone} />
            <div>
              <div style={styles.cardTipoTitulo}>Relatório Diário</div>
              <div style={styles.cardTipoSub}>Relatório completo do caixa do dia</div>
            </div>
          </div>

          <div
            onClick={() => setTipoRelatorio('MENSAL')}
            style={{ ...styles.cardTipo, ...(tipoRelatorio === 'MENSAL' && styles.cardTipoAtivo) }}
          >
            <FaChartBar style={styles.cardTipoIcone} />
            <div>
              <div style={styles.cardTipoTitulo}>Relatório Mensal Completo</div>
              <div style={styles.cardTipoSub}>Serviços, ordens, valores e despesas do mês</div>
            </div>
          </div>

          <div
            onClick={() => setTipoRelatorio('RESUMIDO')}
            style={{ ...styles.cardTipo, ...(tipoRelatorio === 'RESUMIDO' && styles.cardTipoAtivo) }}
          >
            <FaClipboardList style={styles.cardTipoIcone} />
            <div>
              <div style={styles.cardTipoTitulo}>Relatório Mensal Resumido</div>
              <div style={styles.cardTipoSub}>Quantidades, valores e valor líquido do mês</div>
            </div>
          </div>
        </div>

        {tipoRelatorio === 'DIARIO' && (
          <RelatorioDiario getAuthHeader={getAuthHeader} addToast={addToast} />
        )}
        {tipoRelatorio === 'MENSAL' && (
          <RelatorioMensal getAuthHeader={getAuthHeader} addToast={addToast} />
        )}
        {tipoRelatorio === 'RESUMIDO' && (
          <RelatorioResumido getAuthHeader={getAuthHeader} addToast={addToast} />
        )}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// RELATÓRIO DIÁRIO
// ══════════════════════════════════════════════════════════════
// ── Reaproveita EXATAMENTE o endpoint já existente do Relatório de Caixa:
// GET /ordemservico/relatorio-caixa/{dataInicio}/{dataFim}/pdf (blob)
// Enviando a mesma data em início e fim para gerar o relatório de um único dia.
const RelatorioDiario = ({ getAuthHeader, addToast }) => {
  const [data, setData] = useState(dataHojeFormatada());
  const [gerando, setGerando] = useState(false);

  const gerarRelatorio = async () => {
    if (!data) {
      addToast('warning', 'Selecione uma data');
      return;
    }
    setGerando(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/ordemservico/relatorio-caixa/${data}/${data}/pdf`,
        { ...getAuthHeader(), responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const novaJanela = window.open(url, '_blank');
      if (!novaJanela) {
        const link = document.createElement('a');
        link.href = url;
        link.download = `relatorio-diario-${data}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      addToast('success', 'Relatório gerado com sucesso!');
    } catch (err) {
      console.error('Erro ao gerar relatório diário:', err);
      addToast('error', 'Erro ao gerar relatório: ' + (err.response?.data?.message || err.message));
    } finally {
      setGerando(false);
    }
  };

  return (
    <div style={styles.painel}>
      <h3 style={styles.painelTitulo}><FaCalendarDay style={{ marginRight: 8 }} />Relatório Diário de Caixa</h3>

      <label style={styles.label}>Data</label>
      <input
        type="date"
        value={data}
        onChange={(e) => setData(e.target.value)}
        max={dataHojeFormatada()}
        style={styles.inputData}
      />
      <p style={styles.hint}>Selecione o dia para gerar o relatório</p>

      <div style={styles.infoBox}>
        <div style={styles.infoBoxTitulo}><FaListAlt style={{ marginRight: 8 }} />Informações do relatório:</div>
        <ul style={styles.infoBoxLista}>
          <li>Ordens de serviço do dia (agrupadas por cliente)</li>
          <li>Serviços e valores lançados no período</li>
          <li>Resumo financeiro (entradas, saídas e total do dia)</li>
        </ul>
      </div>

      <button onClick={gerarRelatorio} style={styles.botaoGerar} disabled={gerando}>
        <FaPrint /> {gerando ? 'Gerando...' : 'Gerar Relatório'}
      </button>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// RELATÓRIO MENSAL COMPLETO
// ══════════════════════════════════════════════════════════════
// ── CONTRATO ESPERADO DO BACK (a implementar, seguindo o mesmo padrão de
//    nomenclatura já usado em /ordemservico/relatorio-caixa):
// GET /ordemservico/relatorio-mensal/{ano}/{mes}
//    -> { servicos: [{ descricao, quantidade, valorTotal }], totalGeralServicos }
// GET /ordemservico/relatorio-mensal/{ano}/{mes}/pdf  (blob, para impressão)
//
// As despesas do mês reaproveitam o contrato já definido na tela de Despesas:
// GET /despesas/mes/{ano}/{mes}
const RelatorioMensal = ({ getAuthHeader, addToast }) => {
  const [mesFiltro, setMesFiltro] = useState(mesAtualFormatado());
  const [servicos, setServicos] = useState([]);
  const [totalGeralServicos, setTotalGeralServicos] = useState(0);
  const [despesas, setDespesas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [gerandoPdf, setGerandoPdf] = useState(false);

  const carregarDadosMensais = async () => {
    const [ano, mes] = mesFiltro.split('-').map(Number);
    setLoading(true);
    setError(null);
    try {
      const [respServicos, respDespesas] = await Promise.all([
        axios.get(`${API_BASE_URL}/ordemservico/relatorio-mensal/${ano}/${mes}`, getAuthHeader()),
        axios.get(`${API_BASE_URL}/despesas/mes/${ano}/${mes}`, getAuthHeader())
      ]);
      setServicos(respServicos.data?.servicos || []);
      setTotalGeralServicos(respServicos.data?.totalGeralServicos || 0);
      setDespesas(respDespesas.data || []);
    } catch (err) {
      console.error('Erro ao carregar relatório mensal:', err);
      setError(err.response?.data?.message || 'Erro ao carregar dados do relatório mensal');
      setServicos([]);
      setTotalGeralServicos(0);
      setDespesas([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarDadosMensais();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mesFiltro]);

  const totalDespesas = despesas.reduce((soma, d) => soma + (d.valor || 0), 0);
  const saldoFinal = totalGeralServicos - totalDespesas;

  const gerarRelatorioPdf = async () => {
    const [ano, mes] = mesFiltro.split('-').map(Number);
    setGerandoPdf(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/ordemservico/relatorio-mensal/${ano}/${mes}/pdf`,
        { ...getAuthHeader(), responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const novaJanela = window.open(url, '_blank');
      if (!novaJanela) {
        const link = document.createElement('a');
        link.href = url;
        link.download = `relatorio-mensal-${mesFiltro}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      addToast('success', 'Relatório gerado com sucesso!');
    } catch (err) {
      console.error('Erro ao gerar relatório mensal:', err);
      addToast('error', 'Erro ao gerar relatório: ' + (err.response?.data?.message || err.message));
    } finally {
      setGerandoPdf(false);
    }
  };

  return (
    <div style={styles.painel}>
      <h3 style={styles.painelTitulo}><FaChartBar style={{ marginRight: 8 }} />Relatório Mensal Completo</h3>

      <label style={styles.label}>Mês</label>
      <input
        type="month"
        value={mesFiltro}
        onChange={(e) => setMesFiltro(e.target.value)}
        style={styles.inputData}
      />
      <p style={styles.hint}>{nomeMesAno(mesFiltro)}</p>

      {error && <div style={styles.errorMessage}>{error}</div>}

      {loading ? (
        <p style={styles.loading}>Carregando dados do mês...</p>
      ) : (
        <>
          {/* Serviços do mês */}
          <div style={styles.secaoRelatorio}>
            <h4 style={styles.secaoTitulo}><FaMoneyBillWave style={{ marginRight: 8 }} />Serviços (ordens geradas)</h4>
            {servicos.length > 0 ? (
              <div style={styles.tabelaWrapper}>
                <table style={styles.tabela}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Serviço</th>
                      <th style={{ ...styles.th, textAlign: 'center' }}>Qtd. de Ordens</th>
                      <th style={{ ...styles.th, textAlign: 'right' }}>Valor Somado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {servicos.map((s, idx) => (
                      <tr key={idx} style={styles.tr}>
                        <td style={styles.td}>{s.descricao}</td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>{s.quantidade}</td>
                        <td style={{ ...styles.td, textAlign: 'right', fontWeight: '600', color: '#008080' }}>
                          {numeroParaReais(s.valorTotal)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td style={styles.tdTotalLabel} colSpan={2}>Total Geral de Serviços</td>
                      <td style={styles.tdTotalValor}>{numeroParaReais(totalGeralServicos)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <p style={styles.noResults}>Nenhum serviço registrado neste mês</p>
            )}
          </div>

          {/* Despesas do mês */}
          <div style={styles.secaoRelatorio}>
            <h4 style={styles.secaoTitulo}><FaTag style={{ marginRight: 8 }} />Despesas</h4>
            {despesas.length > 0 ? (
              <div style={styles.tabelaWrapper}>
                <table style={styles.tabela}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Descrição</th>
                      <th style={styles.th}>Categoria</th>
                      <th style={{ ...styles.th, textAlign: 'right' }}>Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {despesas.map(d => (
                      <tr key={d.idDespesa} style={styles.tr}>
                        <td style={styles.td}>{d.descricao}</td>
                        <td style={styles.td}>
                          <span style={styles.categoriaTag}>{d.categoria}</span>
                        </td>
                        <td style={{ ...styles.td, textAlign: 'right', fontWeight: '600', color: '#c0392b' }}>
                          {numeroParaReais(d.valor)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td style={styles.tdTotalLabel} colSpan={2}>Total de Despesas</td>
                      <td style={{ ...styles.tdTotalValor, color: '#c0392b' }}>{numeroParaReais(totalDespesas)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <p style={styles.noResults}>Nenhuma despesa registrada neste mês</p>
            )}
          </div>

          {/* Saldo final */}
          <div style={{ ...styles.saldoBox, ...(saldoFinal >= 0 ? styles.saldoPositivo : styles.saldoNegativo) }}>
            <div style={styles.saldoLinha}>
              <FaWallet style={{ marginRight: 8 }} />
              <span>Resultado do Mês (Serviços - Despesas)</span>
            </div>
            <div style={styles.saldoValor}>
              {saldoFinal >= 0 ? <FaArrowUp style={{ marginRight: 8 }} /> : <FaArrowDown style={{ marginRight: 8 }} />}
              {numeroParaReais(saldoFinal)}
            </div>
          </div>

          <button onClick={gerarRelatorioPdf} style={styles.botaoGerar} disabled={gerandoPdf}>
            <FaPrint /> {gerandoPdf ? 'Gerando...' : 'Gerar Relatório PDF'}
          </button>
        </>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// RELATÓRIO MENSAL RESUMIDO
// ══════════════════════════════════════════════════════════════
// GET /ordemservico/relatorio-resumido/{ano}/{mes}
//    -> { quantidadeOrdens, totalOrdens, quantidadeDespesas, totalDespesas, valorLiquido }
// GET /ordemservico/relatorio-resumido/{ano}/{mes}/pdf  (blob, para impressão)
// As entradas (ordens) usam o mesmo cálculo do Relatório Mensal Completo.
const RelatorioResumido = ({ getAuthHeader, addToast }) => {
  const [mesFiltro, setMesFiltro] = useState(mesAtualFormatado());
  const [resumo, setResumo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [gerandoPdf, setGerandoPdf] = useState(false);

  const carregarResumo = async () => {
    const [ano, mes] = mesFiltro.split('-').map(Number);
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/ordemservico/relatorio-resumido/${ano}/${mes}`,
        getAuthHeader()
      );
      setResumo(response.data);
    } catch (err) {
      console.error('Erro ao carregar relatório resumido:', err);
      setError(err.response?.data?.message || 'Erro ao carregar dados do relatório resumido');
      setResumo(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarResumo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mesFiltro]);

  const valorLiquido = resumo?.valorLiquido || 0;

  const gerarRelatorioPdf = async () => {
    const [ano, mes] = mesFiltro.split('-').map(Number);
    setGerandoPdf(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/ordemservico/relatorio-resumido/${ano}/${mes}/pdf`,
        { ...getAuthHeader(), responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const novaJanela = window.open(url, '_blank');
      if (!novaJanela) {
        const link = document.createElement('a');
        link.href = url;
        link.download = `relatorio-resumido-${mesFiltro}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      addToast('success', 'Relatório gerado com sucesso!');
    } catch (err) {
      console.error('Erro ao gerar relatório resumido:', err);
      addToast('error', 'Erro ao gerar relatório: ' + (err.response?.data?.message || err.message));
    } finally {
      setGerandoPdf(false);
    }
  };

  return (
    <div style={styles.painel}>
      <h3 style={styles.painelTitulo}><FaClipboardList style={{ marginRight: 8 }} />Relatório Mensal Resumido</h3>

      <label style={styles.label}>Mês</label>
      <input
        type="month"
        value={mesFiltro}
        onChange={(e) => setMesFiltro(e.target.value)}
        style={styles.inputData}
      />
      <p style={styles.hint}>{nomeMesAno(mesFiltro)}</p>

      {error && <div style={styles.errorMessage}>{error}</div>}

      {loading ? (
        <p style={styles.loading}>Carregando dados do mês...</p>
      ) : (
        <>
          {resumo && (
            <>
              <div style={styles.secaoRelatorio}>
                <div style={styles.tabelaWrapper}>
                  <table style={styles.tabela}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Descrição</th>
                        <th style={{ ...styles.th, textAlign: 'center' }}>Quantidade</th>
                        <th style={{ ...styles.th, textAlign: 'right' }}>Valor Somado</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={styles.tr}>
                        <td style={styles.td}>Ordens de serviço (entradas)</td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>{resumo.quantidadeOrdens}</td>
                        <td style={{ ...styles.td, textAlign: 'right', fontWeight: '600', color: '#008080' }}>
                          {numeroParaReais(resumo.totalOrdens)}
                        </td>
                      </tr>
                      <tr style={styles.tr}>
                        <td style={styles.td}>Despesas (saídas)</td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>{resumo.quantidadeDespesas}</td>
                        <td style={{ ...styles.td, textAlign: 'right', fontWeight: '600', color: '#c0392b' }}>
                          {numeroParaReais(resumo.totalDespesas)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={{ ...styles.saldoBox, ...(valorLiquido >= 0 ? styles.saldoPositivo : styles.saldoNegativo) }}>
                <div style={styles.saldoLinha}>
                  <FaWallet style={{ marginRight: 8 }} />
                  <span>Valor Líquido do Mês (Entradas - Saídas)</span>
                </div>
                <div style={styles.saldoValor}>
                  {valorLiquido >= 0 ? <FaArrowUp style={{ marginRight: 8 }} /> : <FaArrowDown style={{ marginRight: 8 }} />}
                  {numeroParaReais(valorLiquido)}
                </div>
              </div>
            </>
          )}

          <button onClick={gerarRelatorioPdf} style={styles.botaoGerar} disabled={gerandoPdf}>
            <FaPrint /> {gerandoPdf ? 'Gerando...' : 'Gerar Relatório PDF'}
          </button>
        </>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// ESTILOS — paleta Nexus Despachante (#004d4d / #008080 / #20b2aa)
// ══════════════════════════════════════════════════════════════
const TEAL_DARK = '#004d4d';
const TEAL = '#008080';
const TEAL_LIGHT_ACCENT = '#20b2aa';

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    color: '#333',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
  },
  content: {
    padding: '24px',
    maxWidth: '1000px',
    margin: '0 auto',
    width: '100%'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px'
  },
  titleIcon: {
    fontSize: '26px',
    color: TEAL
  },
  title: {
    fontSize: '22px',
    margin: 0,
    color: TEAL_DARK,
    fontWeight: '700'
  },

  cardsSelecao: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: '16px',
    marginBottom: '20px'
  },
  cardTipo: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    backgroundColor: 'white',
    padding: '18px',
    borderRadius: '10px',
    border: '2px solid #e5e7eb',
    cursor: 'pointer',
    boxShadow: '0 2px 4px rgba(0,0,0,0.06)'
  },
  cardTipoAtivo: {
    borderColor: TEAL,
    backgroundColor: '#f0fdfa'
  },
  cardTipoIcone: {
    fontSize: '24px',
    color: TEAL,
    flexShrink: 0
  },
  cardTipoTitulo: {
    fontSize: '15px',
    fontWeight: '700',
    color: TEAL_DARK
  },
  cardTipoSub: {
    fontSize: '12px',
    color: '#7f8c8d',
    marginTop: '2px'
  },

  painel: {
    backgroundColor: 'white',
    padding: '24px',
    borderRadius: '10px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
  },
  painelTitulo: {
    fontSize: '17px',
    margin: '0 0 18px 0',
    color: TEAL_DARK,
    display: 'flex',
    alignItems: 'center',
    fontWeight: '700',
    borderBottom: `2px solid ${TEAL}22`,
    paddingBottom: '12px'
  },

  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    fontSize: '14px',
    color: '#34495e'
  },
  inputData: {
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px',
    minWidth: '220px'
  },
  hint: {
    fontSize: '12px',
    color: '#95a5a6',
    marginTop: '6px',
    marginBottom: '20px'
  },

  infoBox: {
    backgroundColor: '#f8f9fa',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '20px'
  },
  infoBoxTitulo: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: '700',
    fontSize: '14px',
    color: '#34495e',
    marginBottom: '8px'
  },
  infoBoxLista: {
    margin: 0,
    paddingLeft: '20px',
    fontSize: '13px',
    color: '#5b6b73',
    lineHeight: '1.8'
  },

  errorMessage: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: '12px',
    borderRadius: '4px',
    marginBottom: '20px'
  },
  loading: {
    textAlign: 'center',
    padding: '30px',
    color: '#7f8c8d'
  },
  noResults: {
    textAlign: 'center',
    padding: '20px',
    color: '#95a5a6',
    fontStyle: 'italic'
  },

  secaoRelatorio: {
    marginBottom: '24px'
  },
  secaoTitulo: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '15px',
    fontWeight: '700',
    color: TEAL_DARK,
    marginBottom: '12px'
  },

  tabelaWrapper: {
    overflowX: 'auto'
  },
  tabela: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px'
  },
  th: {
    textAlign: 'left',
    padding: '10px 12px',
    borderBottom: `2px solid ${TEAL}22`,
    color: TEAL_DARK,
    fontWeight: '700',
    fontSize: '13px'
  },
  tr: {
    borderBottom: '1px solid #f0f0f0'
  },
  td: {
    padding: '10px 12px',
    verticalAlign: 'middle'
  },
  tdTotalLabel: {
    padding: '12px',
    fontWeight: '700',
    color: TEAL_DARK,
    borderTop: `2px solid ${TEAL}33`
  },
  tdTotalValor: {
    padding: '12px',
    textAlign: 'right',
    fontWeight: '700',
    fontSize: '15px',
    color: TEAL,
    borderTop: `2px solid ${TEAL}33`
  },
  categoriaTag: {
    backgroundColor: '#e0f2f1',
    color: TEAL_DARK,
    padding: '3px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600'
  },

  saldoBox: {
    borderRadius: '10px',
    padding: '18px 20px',
    marginBottom: '20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '10px'
  },
  saldoPositivo: {
    backgroundColor: '#e8f5e9',
    border: '1px solid #a5d6a7'
  },
  saldoNegativo: {
    backgroundColor: '#ffebee',
    border: '1px solid #ef9a9a'
  },
  saldoLinha: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    fontWeight: '600',
    color: '#2c3e50'
  },
  saldoValor: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '22px',
    fontWeight: '700',
    color: '#2c3e50'
  },

  botaoGerar: {
    width: '100%',
    padding: '14px',
    backgroundColor: TEAL,
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    fontSize: '15px',
    fontWeight: '700'
  }
};

export default Relatorios;
